// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import apiFetch from '@wordpress/api-fetch';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Add the nonce as middleware. This automatically adds the `X-WP-Nonce` header to requests.
apiFetch.use( apiFetch.createNonceMiddleware( UncannyAutomatorBackend.rest.nonce ) );

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-item-action-resend-button' )
export class AutomatorLogActionResendButton extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: Number, attribute: 'item-id' } ) accessor itemID = '';

	@property( { type: Number, attribute: 'item-log-id' } ) accessor itemLogID = '';

	@property( { type: String, attribute: false } ) accessor fatalErrorMessage = false;

	@property( { type: Boolean } ) accessor loading = false;

	// This property is used to indicate to the web component that it shouldn't try to retrieve data from the server
	// and that it should use the data provided when it was created
	@property( { type: Boolean, attribute: 'import-mode' } ) accessor isImportMode = false;

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			${ this._templateButton }
			${ this._templateError }
		`;
	}

	/**
	 * Handles the resend functionality
	 */
	handleResend() {
		// Check if it's loading, then abort
		if ( this.loading ) {
			return;
		}

		// As soon as the call starts, set the loading animation
		this.loading = true;
		this.fatalErrorMessage = '';

		// Start call
		apiFetch( {
			url: `${ UncannyAutomatorBackend.rest.url }/resend_api_request`,
			method: 'POST',
			data: {
				// eslint-disable-next-line camelcase
				item_log_id: this.itemLogID
			}
		} )
			.then( ( response ) => {
				// Stop loading animation
				this.loading = false;

				// Request data refresh
				this.requestDataRefresh();
			} )
			.catch( error => {
				// Show a global error
				this.fatalErrorMessage = ! isEmpty( error.message ) ? error.message : escapeHTML( __( 'Unknown error', 'uncanny-automator' ) );

				// Stop loading animation
				this.loading = false;

				// Request data refresh
				this.requestDataRefresh();

				console.error( error );
			} );
	}

	/**
	 * Send request to main component to refresh the data
	 */
	requestDataRefresh() {
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-data-refresh', {
				bubbles: true,
				composed: true,
				detail: {
					// The ID used as the sidebar ID
					requestedBy: `item-${ this.itemID }`,
				}
			} )
		);
	}

	/**
	 * Creates the resend button
	 * It'll create a disabled button if it's import mode
	 *
	 * @return {TemplateResult} The resend button
	 */
	get _templateButton() {
		// Check if it's import mode
		if ( this.isImportMode ) {
			return html`
				<uo-tooltip>
					${ escapeHTML( __( 'Resending is not possible in import mode', 'uncanny-automator' ) ) }

					<uo-button
						slot="target"
						size="small"
						color="secondary"
						disabled
					>
						<uo-icon id="repeat"></uo-icon> ${ escapeHTML( __( 'Resend', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-tooltip>
			`;
		}

		return html`
			<uo-button
				size="small"
				color="secondary"

				?loading=${ this.loading }

				@click=${ this.handleResend }
			>
				<uo-icon id="repeat"></uo-icon> ${ escapeHTML( __( 'Resend', 'uncanny-automator' ) ) }
			</uo-button>
		`;
	}

	/**
	 * Generates a template for the error component.
	 *
	 * @return {TemplateResult|undefined} - A lit-html template result for the error component, or undefined if there is no error message. The template includes a div with the class 'error' and the error message.
	 */
	get _templateError() {
		// Check if there is an error
		if ( isEmpty( this.fatalErrorMessage ) ) {
			return;
		}

		return html`
			<div class="error">
				${ this.fatalErrorMessage }
			</div>
		`;
	}
}
